<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import FloadMenuContainer from './FloadMenuContainer/index.vue'
interface Props {
  position: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  class?: /** @vue-ignore */ HTMLAttributes['class']
  closeOnSelect?: boolean
  containerClass?:/** @vue-ignore */ HTMLAttributes['class']
  offset?: number
}

interface Emits {
  (event: 'onSelect', value: unknown): boolean
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const containerRef = ref<HTMLDivElement | null>(null)

const id = randomID()
const bounds = ref<DOMRect | null>(null)

defineOptions({
  name: 'FloatMenu',
})
const trigger = ref<HTMLDivElement | null>(null)

const [isOpen, toggle] = useToggle()

useEventListener('resize', () => {
  bounds.value = document.body.getBoundingClientRect()
  if (isOpen.value)
    handlerPosition()
})

onMounted(() => {
  bounds.value = document.body.getBoundingClientRect()
})

const fn = useDebounceFn(() => {
  if (isOpen.value)
    handlerPosition()
}, 50)

const containerStyle = ref<Record<string, any>>(
  {
    position: 'absolute',
    zIndex: '-999',
    display: 'none',
    top: '0',
    left: '0',
    opacity: 0,
    pointerEvents: 'none',
    minWidth: '0',
  },
)
watch(isOpen, (open) => {
  if (open) {
    fn()
    containerStyle.value = {
      ...containerStyle.value,
      display: 'block',
      zIndex: '-999',
      opacity: 0,
    }
  }
  else {
    containerStyle.value = {
      ...containerStyle.value,
      display: 'none',
      zIndex: '-999',
      opacity: 0,
    }
  }
})

async function handlerPosition() {
  if (trigger.value && containerRef.value) {
    const triggerRect = trigger.value.getBoundingClientRect()
    if (!triggerRect || triggerRect.width === 0 || triggerRect.height === 0)
      return
    containerStyle.value = {
      ...containerStyle.value,
      display: 'block',
      opacity: 1,
    }

    await nextTick()
    const containerRect = containerRef.value.getBoundingClientRect()
    const { position } = props

    if (containerRect.width === 0)
      containerRect.width = triggerRect.width

    const basePosition = position.split('-')[0]
    let align = position.split('-')[1]

    const basePositionCordinates = {
      x: 0,
      y: 0,

    }
    const cordinates = {
      x: 0,
      y: 0,
    }

    switch (basePosition) {
      case 'top':
        basePositionCordinates.x = triggerRect.x + triggerRect.width / 2
        basePositionCordinates.y = triggerRect.y - containerRect.height - (props.offset || 0)
        break
      case 'bottom':
        basePositionCordinates.x = triggerRect.x
        basePositionCordinates.y = triggerRect.y + triggerRect.height + (props.offset || 0)
        break
      case 'left':
        basePositionCordinates.x = triggerRect.x - containerRect.width - (props.offset || 0)
        basePositionCordinates.y = triggerRect.y
        break
      case 'right':
        basePositionCordinates.x = triggerRect.x + triggerRect.width + (props.offset || 0)
        basePositionCordinates.y = triggerRect.y + triggerRect.height / 2
        break
      default:
        basePositionCordinates.x = triggerRect.x + triggerRect.width / 2
        basePositionCordinates.y = triggerRect.y - triggerRect.height + containerRect.height + (props.offset || 0)
        break
    }

    if (!align)
      align = 'center'

    switch (align) {
      case 'center':
        if (basePosition === 'top' || basePosition === 'bottom') {
          const triggerWidthisBigger = triggerRect.width > containerRect.width
          cordinates.x = triggerWidthisBigger ? (triggerRect.width / 2 - basePositionCordinates.x) - (containerRect.width / 2) : basePositionCordinates.x - containerRect.width / 2
          cordinates.y = basePositionCordinates.y
        }
        else if (basePosition === 'left' || basePosition === 'right') {
          cordinates.y = basePositionCordinates.y - containerRect.height / 2
          cordinates.x = basePositionCordinates.x
        }
        break
      case 'left':
        if (basePosition === 'top') {
          cordinates.x = basePositionCordinates.x - containerRect.width - triggerRect.width / 2 - (props.offset || 0)
          cordinates.y = basePositionCordinates.y
        }
        else {
          cordinates.y = basePositionCordinates.y
          cordinates.x = basePositionCordinates.x - containerRect.width
        }

        break
      case 'right':
        if (basePosition === 'top') {
          cordinates.x = basePositionCordinates.x + triggerRect.width / 2
          cordinates.y = basePositionCordinates.y
        }
        else {
          cordinates.x = basePositionCordinates.x + triggerRect.width + (props.offset || 0)
          cordinates.y = basePositionCordinates.y
        }
        break
      default:
        cordinates.x = basePositionCordinates.x - containerRect.width / 2
        cordinates.y = basePositionCordinates.y - containerRect.height / 2
        break
    }

    const bounds = document.body.getBoundingClientRect()

    const hasColision = {
      top: cordinates.y < 0,
      bottom: cordinates.y + containerRect.height > bounds.height,
      left: cordinates.x < 0,
      right: cordinates.x + containerRect.width > bounds.width,
    }
    if (
      Object.values(hasColision).some(value => value)
    ) {
      if (hasColision.top)
        cordinates.y = triggerRect.y + triggerRect.height + (props.offset || 0)
      if (hasColision.bottom)
        cordinates.y = triggerRect.y - containerRect.height - (props.offset || 0)
      if (hasColision.left)
        cordinates.x = basePositionCordinates.x
      if (hasColision.right)
        cordinates.x = triggerRect.x - containerRect.width - (props.offset || 0)
    }

    containerStyle.value = {
      ...containerStyle.value,
      top: `${cordinates.y}px`,
      left: `${cordinates.x}px`,
      display: 'block',
      zIndex: '999',
      position: 'absolute',
      opacity: 1,
      pointerEvents: 'auto',
      minWidth: `${triggerRect.width}px`,
    }
  }
}

const handlerSelect = (value: unknown, ignoreClose = false) => {
  if (props.closeOnSelect && !ignoreClose)
    toggle(false)
  emits('onSelect', value)
}

watch(trigger, (node) => {
  if (node) {
    const rect = node.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0)
      return
    containerStyle.value = {
      ...containerStyle.value,
      top: `${rect.y}px`,
      left: `${rect.x}px`,
      zIndex: '-999',
    }
  }
})

defineExpose({
  toggle,
  isOpen,
})
</script>

<template>
  <div
    :id="`menu-button-${id}`"
    ref="trigger"
    role="button"
    class="float-menu"
    :class="$props.class"
    :aria-haspopup="true"
    :aria-expanded="isOpen"
    :aria-controls="`menu-${id}`"
  >
    <slot :toggle="toggle" :is-open="isOpen" />
  </div>
  <Teleport v-if="isOpen" to="body">
    <div class="absolute  w-full h-full z-900 top-0 left-0" @click.stop="toggle(false)" />
    <div
      ref="containerRef" :style="{
        zIndex: containerStyle.zIndex,
        top: containerStyle.top,
        left: containerStyle.left,
        display: containerStyle.display,
        opacity: containerStyle.opacity,
        minWidth: containerStyle.minWidth,
        position: 'absolute',
        pointerEvents: containerStyle.pointerEvents,
      }"
    >
      <slot name="content" :is-open="isOpen" @on-select="handlerSelect">
        <FloadMenuContainer
          :class="containerClass" :close-on-select="closeOnSelect" :container-ref="containerRef" :is-open="isOpen"
          :menu-id="id"
          @on-select="handlerSelect"
        >
          <slot name="options" :handler-select="handlerSelect" />
        </FloadMenuContainer>
      </slot>
      <slot name="extra-content" />
    </div>
  </Teleport>
</template>

<style module lang="css">
.float-menu {
  position: relative ;
}
</style>
